/**
 * Scoped Knowledge Routing API — the "Project knowledge" / "Organization
 * knowledge" review sections (spec CHAOS_KNOWLEDGE_ROUTING_ARCHITECTURE.md
 * Part B, migration 0113 chaos.knowledge_additions).
 *
 * Structural sibling of lib/api/role-improvements.ts — same propose → inbox →
 * approve → inject → revert lifecycle, but for the PROJECT and ORG durable
 * scopes instead of the role. Flag-gated server-side (KNOWLEDGE_ROUTING_ENABLED,
 * default OFF) — every route 404s when the flag is OFF, so the review sections +
 * badges degrade gracefully on 404 (render nothing).
 *
 * An "addition" is one git-style scoped learning: proposed by an agent
 * in-session via chaos.remember(scope), approved (→ gets a per-target commit
 * number `seq`, shown as `KP7` for project / `KO3` for org), rejected, reverted,
 * or superseded. Approved additions are injected into every future spawn on that
 * project (any card) or in that org, fenced server-side by the session→card
 * chain — approval is the gate that puts a durable fact into the model's mind.
 *
 * Wire contract MIRRORS the gateway's mapAdditionRow (BUILD #1,
 * apps/chaos-gateway/src/knowledge-routing.ts): fields = camelCase, provenance
 * nested, poisonFlags {family,severity,message}, pendingCount, budget,
 * canApprove. When a field name is uncertain, the role-improvements.ts camelCase
 * names are the template.
 */
import { apiJson } from './client';

/** The two durable scopes a knowledge review section manages. `project` maps to
 *  a board (chaos.projects) in the current data model; `org` is the whole
 *  organization. (role / card are re-target destinations, not review scopes.) */
export type KnowledgeScope = 'project' | 'org';

export type KnowledgeAdditionStatus =
  | 'proposed'
  | 'approved'
  | 'rejected'
  | 'reverted'
  | 'superseded';
/** `fact` = a small injected statement; `doc` = a summary line pointing at a
 *  KB document (the bulk lives in the knowledge base, retrieved via kb_search). */
export type KnowledgeAdditionKind = 'fact' | 'doc';
export type ScopeConfidence = 'confident' | 'unsure';
export type ProposedBy = 'agent' | 'user' | 'distiller';

/** Re-target destinations (spec §3.5): the reviewer can move a proposal between
 *  these scopes BEFORE approving — the human backstop for agent
 *  misclassification. project/org re-resolve their target from the proposal's
 *  session→card chain; `role` files it as a role addition (the proposing agent's
 *  role); `card` demotes it to a card note on the origin card. */
export type RetargetScope = 'project' | 'org' | 'role' | 'card';

/** A poison-guard hit surfaced on a proposal. `blocking` families
 *  (pipe-to-shell / exfil / instruction-override / identity-subversion) require
 *  an explicit "I reviewed this" confirm before Approve; `info` families
 *  (standing-every / from-now-on) render as an informational chip only. */
export interface PoisonFlag {
  family: string;
  severity: 'info' | 'blocking';
  message?: string;
}

/** Full, server-resolved provenance (git-blame for a learning). Never
 *  agent-supplied — resolved from the JWT + session→card chain at propose time. */
export interface KnowledgeAdditionProvenance {
  proposedBy: ProposedBy;
  proposedByUserName: string | null;
  proposedByRoleName: string | null;
  sessionId: string | null;
  sessionRunId: string | null;
  cardId: string | null;
  cardTitle: string | null;
  boardId: string | null;
  boardName: string | null;
  projectId: string | null;
  projectName: string | null;
  originOrgId: string | null;
  originOrgName: string | null;
}

export interface KnowledgeAddition {
  id: string;
  scope: KnowledgeScope;
  kind: KnowledgeAdditionKind;
  title: string;
  content: string;
  scopeConfidence: ScopeConfidence;
  status: KnowledgeAdditionStatus;
  /** per-target commit number, assigned at approval; null while proposed.
   *  Rendered as `KP<seq>` (project) / `KO<seq>` (org). */
  seq: number | null;
  supersedesId: string | null;
  supersedesSeq: number | null;
  revertOfId: string | null;
  userComment: string | null;
  rationale: string | null;
  poisonFlags: PoisonFlag[];
  editedBeforeApproval: boolean;
  originalContent: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  approvedAt: string | null;
  revertedAt: string | null;
  revertedByName: string | null;
  provenance: KnowledgeAdditionProvenance;
}

/** Prompt-injection budget footer (spec §3.4 — project ≤ 12 KB, org ≤ 12 KB). */
export interface KnowledgeBudget {
  usedBytes: number;
  maxBytes: number;
  count: number;
  maxCount: number;
}

export interface KnowledgeAdditionsResponse {
  additions: KnowledgeAddition[];
  pendingCount: number;
  budget: KnowledgeBudget;
  /** Server-resolved approval authority for THIS scope target (spec §3.1):
   *  project = project/org admin or account owner; org = org admin or account
   *  owner. Read-only viewers get false and see the proposals but no actions. */
  canApprove?: boolean;
}

export type KnowledgeStatusFilter = 'proposed' | 'approved' | 'all';

export interface DecideKnowledgeInput {
  action: 'approve' | 'reject';
  /** Owner-edited content — presence flips edited_before_approval + preserves original. */
  content?: string;
  title?: string;
  note?: string;
}

export interface RetargetKnowledgeInput {
  toScope: RetargetScope;
  /** Only meaningful when toScope='role' and the server can't derive the role
   *  from provenance; normally omitted (server resolves from the chain). */
  roleId?: string | null;
}

function base(scope: KnowledgeScope, targetId: string): string {
  return `/knowledge/${scope}/${targetId}/additions`;
}

export const knowledgeAdditionsApi = {
  /**
   * List a scope target's additions (proposals + history).
   *
   * NORMALISED AT THE DOOR, for the same reason as
   * `roleImprovementsApi.list()` — this is the twin route, and
   * knowledge-review-section.tsx:819 reads it the same way
   * (`q.data.pendingCount ?? q.data.additions.length`). It is done here rather
   * than at that read site because the count is not the only reader: the
   * section maps the list, and each addition's `poisonFlags` is read as
   * `a.poisonFlags.some(...)` at :237 and :456 during render.
   */
  list(
    scope: KnowledgeScope,
    targetId: string,
    status: KnowledgeStatusFilter = 'all',
  ): Promise<KnowledgeAdditionsResponse> {
    return apiJson<KnowledgeAdditionsResponse>(
      `${base(scope, targetId)}?status=${status}`,
    ).then((r) => {
      const additions = (Array.isArray(r?.additions) ? r.additions : []).map((a) => ({
        ...a,
        poisonFlags: Array.isArray(a?.poisonFlags) ? a.poisonFlags : [],
      }));
      return {
        ...r,
        additions,
        pendingCount:
          typeof r?.pendingCount === 'number'
            ? r.pendingCount
            : additions.filter((a) => a.status === 'proposed').length,
      };
    });
  },

  /** Approve / reject a proposal. Approve assigns the next `seq` server-side. */
  decide(
    scope: KnowledgeScope,
    targetId: string,
    additionId: string,
    input: DecideKnowledgeInput,
  ): Promise<KnowledgeAddition> {
    return apiJson<KnowledgeAddition>(
      `${base(scope, targetId)}/${additionId}/decide`,
      { method: 'POST', json: input },
    );
  },

  /** Revert / un-revert an approved addition (drops it out of / back into every
   *  subsequent spawn; the history row is kept either way). */
  setReverted(
    scope: KnowledgeScope,
    targetId: string,
    additionId: string,
    reverted: boolean,
  ): Promise<KnowledgeAddition> {
    return apiJson<KnowledgeAddition>(
      `${base(scope, targetId)}/${additionId}/revert`,
      { method: 'POST', json: { reverted } },
    );
  },

  /** Re-target a still-proposed addition to another scope (project/org/role/card)
   *  before approving — the human backstop for agent misclassification. The row
   *  moves out of this list and reappears under the destination scope. */
  retarget(
    scope: KnowledgeScope,
    targetId: string,
    additionId: string,
    input: RetargetKnowledgeInput,
  ): Promise<KnowledgeAddition> {
    return apiJson<KnowledgeAddition>(
      `${base(scope, targetId)}/${additionId}/retarget`,
      { method: 'POST', json: input },
    );
  },

  /** Consolidate (squash) several approved additions into one merged addition
   *  that supersedes them (spec §3.4 budget footer). */
  consolidate(
    scope: KnowledgeScope,
    targetId: string,
    input: { supersedeIds: string[]; title: string; content: string },
  ): Promise<KnowledgeAddition> {
    return apiJson<KnowledgeAddition>(`${base(scope, targetId)}/consolidate`, {
      method: 'POST',
      json: input,
    });
  },
};

export const knowledgeAdditionsKeys = {
  all: ['knowledge-additions'] as const,
  list: (scope: KnowledgeScope, targetId: string, status: KnowledgeStatusFilter) =>
    [...knowledgeAdditionsKeys.all, scope, targetId, status] as const,
  pending: (scope: KnowledgeScope, targetId: string) =>
    [...knowledgeAdditionsKeys.all, scope, targetId, 'proposed'] as const,
};

/** K-number chip label for an addition: `KP7` (project) / `KO3` (org). */
export function knowledgeKNumber(a: Pick<KnowledgeAddition, 'scope' | 'seq'>): string {
  const prefix = a.scope === 'project' ? 'KP' : 'KO';
  return `${prefix}${a.seq ?? '—'}`;
}
